import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {httpDeleteConversationMessage} from '../http/conversations.http'
import {applyConversationMessageDeletion} from '../utils/messagesCacheUtils'
import {errorHandler} from '@/utilities/helpers'

export interface DeleteConversationMessagePayload {
    conversationId: string
    messageId: string
}

/**
 * Deletes (soft delete) a conversation message. The sender gets the placeholder immediately
 * from the local cache patch; the other members get it from the `conversation_message:deleted`
 * socket event (see useSocketConversationMessages).
 */
export const useDeleteConversationMessage = (
    options?: UseMutationOptions<void, AxiosError, DeleteConversationMessagePayload>
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({conversationId, messageId}: DeleteConversationMessagePayload) => {
            await httpDeleteConversationMessage(conversationId, messageId)
        },
        onSuccess: (data, variables, context) => {
            applyConversationMessageDeletion(queryClient, variables.conversationId, variables.messageId)
            options?.onSuccess?.(data, variables, context)
        },
        onError: (error, variables, context) => {
            errorHandler(error)
            options?.onError?.(error, variables, context)
        },
        onMutate: options?.onMutate,
        onSettled: options?.onSettled
    })
}
